import * as React from 'react';

import { cn, Col, MultiSelect, Row } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideMapPin } from 'lucide-react';

import { useFacilitiesQuery } from './_graphql/useFacilitiesQuery';
import { useRolesQuery } from './_graphql/useRolesQuery';
import { useUserRoleAssignmentsQuery } from './_graphql/useUserRoleAssignmentsQuery';
import { useUserRoleAssignMutation } from './_graphql/useUserRoleAssignMutation';
import { useUserRoleRevokeMutation } from './_graphql/useUserRoleRevokeMutation';

import {
  DetailPanel,
  EmptyDetailState,
  FormFooter,
  PermissionsEditor,
  SelectionListItem,
  SettingsContent,
  TwoPanelSelectionLayout,
} from '~/modules/settings/_components/_shared';
import { allPermissionModules } from '~/modules/settings/_generated/permissions';
import {
  $discardDraftChanges,
  $selectFacility,
  $updateDraftRoles,
  $updateUserFacilityForm,
  $userId,
  $userFacilityForm,
  type UserFacilityAssignment,
} from '~/modules/settings/_state/userForm.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

// =============================================================================
// Types
// =============================================================================

interface FacilityListItem extends UserFacilityAssignment {
  hasPermissions: boolean;
}

// =============================================================================
// Section Header Component
// =============================================================================

interface SectionHeaderProps {
  title: string;
  count: number;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, count }) => (
  <div className="@SectionHeader px-3 pt-2 pb-1.5">
    <span className="text-xs font-bold tracking-wide text-neutral/50 uppercase">
      {title} ({count})
    </span>
  </div>
);

// =============================================================================
// Facility Detail Panel Component
// =============================================================================

interface FacilityDetailPanelProps {
  facility: FacilityListItem;
  roles: { id: string; name: string }[];
  draftRoleIds: string[];
  onRolesChange: (roleIds: string[]) => void;
  onDiscard: () => void;
  onSave: () => void;
  isDirty: boolean;
  isSaving: boolean;
}

const FacilityDetailPanel: React.FC<FacilityDetailPanelProps> = ({
  facility,
  roles,
  draftRoleIds,
  onRolesChange,
  onDiscard,
  onSave,
  isDirty,
  isSaving,
}) => {
  const t = useTranslations();
  const selectedRoles = React.useMemo(
    () => roles.filter((role) => draftRoleIds.includes(role.id)),
    [roles, draftRoleIds],
  );

  const roleOptions = React.useMemo(
    () => roles.map((role) => ({ key: role.id, label: role.name })),
    [roles],
  );

  const inheritedPermissions = React.useMemo(() => {
    const permissions: Record<string, boolean> = {};

    if (draftRoleIds.length > 0) {
      allPermissionModules.forEach((module) => {
        module.entities.forEach((entity) => {
          entity.permissions.forEach((permission) => {
            if (permission.action === 'Read') {
              permissions[permission.key] = true;
            }
          });
        });
      });
    }

    return permissions;
  }, [draftRoleIds]);

  const roleNames = selectedRoles.map((r) => r.name);
  const readOnlyMessage =
    roleNames.length > 0
      ? t.teamAndPermissions.userDetails.facilities.permissionInherited(
          roleNames.join(', '),
        )
      : t.teamAndPermissions.userDetails.facilities.noRolesAssigned;

  const lastUpdatedText = facility.lastUpdatedAt
    ? t.teamAndPermissions.userDetails.facilities.lastUpdated(
        new Date(facility.lastUpdatedAt).toLocaleDateString(),
        facility.lastUpdatedBy ?? 'Unknown',
      )
    : null;

  return (
    <DetailPanel
      header={
        <div className="border-b border-neutral/15 px-5 py-4">
          <Row gap2 className="items-center">
            <LucideMapPin size={20} className="text-primary" />
            <Col className="gap-0.5">
              <span className="text-lg font-semibold text-neutral">
                {facility.facilityName}
              </span>
              <span className="text-sm text-neutral/60">
                {lastUpdatedText ??
                  t.teamAndPermissions.userDetails.facilities.noUpdatesYet}
              </span>
            </Col>
          </Row>
        </div>
      }
      contentClassName="gap-6 p-5 px-5"
      footer={
        <FormFooter
          onDiscard={onDiscard}
          onSave={onSave}
          isDiscardDisabled={isSaving || !isDirty}
          isSaveDisabled={isSaving || !isDirty}
        />
      }
      className="@FacilityDetailPanel"
    >
      <div>
        <MultiSelect
          label={t.teamAndPermissions.userDetails.facilities.rolesLabel}
          required
          options={roleOptions}
          selectedKeys={draftRoleIds}
          onSelectionChange={onRolesChange}
          placeholder={
            t.teamAndPermissions.userDetails.facilities.selectRolesPlaceholder
          }
        />
      </div>

      <div className="flex-1">
        <PermissionsEditor
          modules={allPermissionModules}
          value={inheritedPermissions}
          readOnly={true}
          readOnlyMessage={readOnlyMessage}
        />
      </div>
    </DetailPanel>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const UserFacilitiesTab = () => {
  const t = useTranslations();
  const toast = useToast();
  const userId = useAtomValue($userId);
  const facilityForm = useAtomValue($userFacilityForm);
  const updateFacilityForm = useSetAtom($updateUserFacilityForm);
  const selectFacility = useSetAtom($selectFacility);
  const updateDraftRoles = useSetAtom($updateDraftRoles);
  const discardDraftChanges = useSetAtom($discardDraftChanges);

  const [searchValue, setSearchValue] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);

  const { data: { facilities = [] } = {} } = useFacilitiesQuery({
    limit: 1000,
    offset: 0,
  });

  const { data: { roles = [] } = {} } = useRolesQuery({
    limit: 1000,
    offset: 0,
  });

  const { data: assignmentsData } = useUserRoleAssignmentsQuery({ userId });
  const rawAssignments = assignmentsData?.assignments ?? [];

  const assignMutation = useUserRoleAssignMutation();
  const revokeMutation = useUserRoleRevokeMutation();

  const prevAssignmentsRef = React.useRef<string>('');

  React.useEffect(() => {
    if (facilities.length === 0) return;

    const assignmentsKey = JSON.stringify(rawAssignments);
    if (assignmentsKey === prevAssignmentsRef.current) return;
    prevAssignmentsRef.current = assignmentsKey;

    const facilityRoleMap = new Map<string, string[]>();
    rawAssignments.forEach((a) => {
      const existing = facilityRoleMap.get(a.facilityId) ?? [];
      existing.push(a.roleId);
      facilityRoleMap.set(a.facilityId, existing);
    });

    const initialAssignments: UserFacilityAssignment[] = facilities.map(
      (fac) => ({
        facilityId: fac.id,
        facilityName: fac.name,
        facilityAddress: fac.address,
        assignedRoleIds: facilityRoleMap.get(fac.id) ?? [],
        lastUpdatedAt: null,
        lastUpdatedBy: null,
      }),
    );

    updateFacilityForm({
      assignments: initialAssignments,
      rawAssignments,
    });
  }, [facilities, rawAssignments, updateFacilityForm]);

  const facilityListItems: FacilityListItem[] = React.useMemo(
    () =>
      facilityForm.assignments.map((assignment) => ({
        ...assignment,
        hasPermissions: assignment.assignedRoleIds.length > 0,
      })),
    [facilityForm.assignments],
  );

  const filteredAndSortedFacilities = React.useMemo(() => {
    const filtered = facilityListItems.filter(
      (fac) =>
        fac.facilityName.toLowerCase().includes(searchValue.toLowerCase()) ||
        fac.facilityAddress.toLowerCase().includes(searchValue.toLowerCase()),
    );

    return [...filtered].sort((a, b) => {
      if (a.hasPermissions && !b.hasPermissions) return -1;
      if (!a.hasPermissions && b.hasPermissions) return 1;
      return a.facilityName.localeCompare(b.facilityName);
    });
  }, [facilityListItems, searchValue]);

  const permissionsSetCount = filteredAndSortedFacilities.filter(
    (fac) => fac.hasPermissions,
  ).length;
  const noPermissionsCount = filteredAndSortedFacilities.filter(
    (fac) => !fac.hasPermissions,
  ).length;

  const handleSelectFacility = (facilityId: string) => {
    if (facilityForm.isDirty) {
      discardDraftChanges();
    }
    selectFacility(facilityId);
  };

  const handleSave = async () => {
    const selectedFacilityId = facilityForm.selectedFacilityId;
    if (!selectedFacilityId) return;

    const assignment = facilityForm.assignments.find(
      (a) => a.facilityId === selectedFacilityId,
    );
    const originalRoleIds = assignment?.assignedRoleIds ?? [];
    const draftRoleIds = facilityForm.draftRoleIds;

    const added = draftRoleIds.filter((id) => !originalRoleIds.includes(id));
    const removed = originalRoleIds.filter((id) => !draftRoleIds.includes(id));

    setIsSaving(true);
    try {
      const promises: Promise<unknown>[] = [];

      for (const roleId of added) {
        promises.push(
          assignMutation.mutateAsync({
            userId,
            roleId,
            facilityId: selectedFacilityId,
          }),
        );
      }

      for (const roleId of removed) {
        const raw = facilityForm.rawAssignments.find(
          (r) => r.roleId === roleId && r.facilityId === selectedFacilityId,
        );
        if (raw) {
          promises.push(revokeMutation.mutateAsync({ assignmentId: raw.id }));
        }
      }

      await Promise.all(promises);
      toast.success(
        t.teamAndPermissions.userDetails.facilities.messages.updateSuccess,
      );
    } catch (error) {
      console.error('Failed to update facility permissions:', error);
      toast.error(
        t.teamAndPermissions.userDetails.facilities.messages.updateError,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    discardDraftChanges();
  };

  const getItemWithSectionHeader = (
    facility: FacilityListItem,
    index: number,
  ): {
    showWithPermissionsHeader: boolean;
    showNoPermissionsHeader: boolean;
  } => {
    const prevFacility =
      index > 0 ? filteredAndSortedFacilities[index - 1] : null;

    return {
      showWithPermissionsHeader:
        facility.hasPermissions &&
        (index === 0 || !!(prevFacility && !prevFacility.hasPermissions)),
      showNoPermissionsHeader:
        !facility.hasPermissions &&
        (index === 0 || !!(prevFacility && prevFacility.hasPermissions)),
    };
  };

  return (
    <SettingsContent size="full" className="@UserFacilitiesTab h-full">
      <TwoPanelSelectionLayout
        items={filteredAndSortedFacilities}
        selectedId={facilityForm.selectedFacilityId}
        onSelect={handleSelectFacility}
        getItemId={(fac) => fac.facilityId}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={
          t.teamAndPermissions.userDetails.facilities.searchPlaceholder
        }
        leftPanelWidth={280}
        renderItem={(facility, isSelected) => {
          const index = filteredAndSortedFacilities.indexOf(facility);
          const { showWithPermissionsHeader, showNoPermissionsHeader } =
            getItemWithSectionHeader(facility, index);

          return (
            <div className="px-2">
              {showWithPermissionsHeader && (
                <SectionHeader
                  title={
                    t.teamAndPermissions.userDetails.facilities.permissionsSet
                  }
                  count={permissionsSetCount}
                />
              )}
              {showNoPermissionsHeader && (
                <SectionHeader
                  title={
                    t.teamAndPermissions.userDetails.facilities.noPermissions
                  }
                  count={noPermissionsCount}
                />
              )}
              <SelectionListItem
                name={facility.facilityName}
                nameClassName={cn(
                  !facility.hasPermissions && !isSelected && 'text-neutral/60!',
                )}
                subtitle={facility.facilityAddress}
                isSelected={isSelected}
              />
            </div>
          );
        }}
        renderDetail={(facility) =>
          facility ? (
            <FacilityDetailPanel
              facility={facility}
              roles={roles}
              draftRoleIds={facilityForm.draftRoleIds}
              onRolesChange={updateDraftRoles}
              onDiscard={handleDiscard}
              onSave={handleSave}
              isDirty={facilityForm.isDirty}
              isSaving={isSaving}
            />
          ) : (
            <EmptyDetailState
              icon={<LucideMapPin />}
              title={
                t.teamAndPermissions.userDetails.facilities.emptyState.title
              }
              subtitle={
                t.teamAndPermissions.userDetails.facilities.emptyState.subtitle
              }
            />
          )
        }
      />
    </SettingsContent>
  );
};
